import React, { useCallback, useEffect, useState } from 'react';
import api from '../utils/api';
import { toast } from 'react-toastify';
import './StuckOrdersModal.css';

// Drill-down for the clickable STUCK tile on the admin overview.
// Lists orders stuck >2h; clicking a row opens the full OrderDetailModal
// (via onOpenOrder) where the admin can refresh/refund.
const StuckOrdersModal = ({ open, onClose, onOpenOrder }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/orders/stuck');
      setRows(res.data?.orders || []);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load stuck orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const refreshAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await api.post('/smm/admin/orders/refresh-status', {});
      toast.success(`Refreshed ${res?.data?.updated ?? 0} orders from provider`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  }, [load]);

  if (!open) return null;

  return (
    <div className="stuck-overlay" onClick={onClose}>
      <div className="stuck-modal" onClick={(e) => e.stopPropagation()}>
        <button className="stuck-close" type="button" onClick={onClose} aria-label="Close">×</button>
        <div className="stuck-head">
          <div>
            <span className="stuck-eyebrow">Needs attention</span>
            <h3 className="stuck-title">Stuck orders <span className="stuck-count">{rows.length}</span></h3>
            <p className="stuck-sub">Non-terminal orders older than 2 hours. Click a row for full detail &amp; refund.</p>
          </div>
          <button className="stuck-refresh" onClick={refreshAll} disabled={refreshing}>
            {refreshing ? 'Refreshing…' : '↻ Refresh all from provider'}
          </button>
        </div>

        {loading ? (
          <div className="stuck-empty">Loading…</div>
        ) : error ? (
          <div className="stuck-error">⚠ {error}</div>
        ) : rows.length === 0 ? (
          <div className="stuck-empty">🎉 Nothing stuck — all orders are moving.</div>
        ) : (
          <div className="stuck-table-wrap">
            <table className="stuck-table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Wallet</th>
                  <th>Service</th>
                  <th>Status</th>
                  <th>Remains</th>
                  <th>Age</th>
                  <th>Charged</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr
                    key={o.db_id}
                    className="stuck-row"
                    role="button"
                    tabIndex={0}
                    onClick={() => onOpenOrder && onOpenOrder(o.db_id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenOrder && onOpenOrder(o.db_id); } }}
                    title="Open full order detail"
                  >
                    <td><strong>{o.internal_order_id}</strong></td>
                    <td><code>{o.wallet ? `${o.wallet.slice(0, 6)}…${o.wallet.slice(-4)}` : '—'}</code></td>
                    <td className="stuck-svc" title={o.service_name}>{o.service_name || '—'}</td>
                    <td>{o.status}</td>
                    <td>{o.remains != null ? Number(o.remains).toLocaleString() : '—'}</td>
                    <td className={o.age_hours >= 24 ? 'stuck-age-bad' : 'stuck-age'}>{o.age_hours}h</td>
                    <td>${Number(o.charged_usd || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StuckOrdersModal;
